import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import d3_geoPath from '../lib/d3-geo-path/index.js'
import * as COLORS from '../utils/colorUtils'
import { interpolateRgb as d3_interpolateRgb } from 'd3-interpolate'
import { select as d3_select } from 'd3-selection'

import {
  geoMercator as d3_geoMercator,
  geoNaturalEarth1 as d3_geoNaturalEarth1,
  geoConicEquidistant as d3_geoConicEquidistant,
  geoEquirectangular as d3_geoEquirectangular,
  geoAlbersUsa as d3_geoAlbersUsa,
  geoAlbers as d3_geoAlbers,
} from 'd3-geo'
const projections = {
  d3_geoMercator,
  d3_geoNaturalEarth1,
  d3_geoConicEquidistant,
  d3_geoAlbersUsa,
  d3_geoEquirectangular,
  d3_geoAlbers,
}
// import geojson from '../data/geojson/spain.geo.json'
import SizeHelper from '../chart_helpers/SizeHelper'
import { isDarkMode } from '../utils/configUtils.js'

export default class ChoroplethChart extends BaseChart {
  static defaultConfig = {
    ItemsHelper: {
      colorScale: 'sequential2',
      colorKey: 'value',
    },
    /**
     * {-EN-} Map id of drawn features
     * {-ES-} Identificación de las características dibujadas del mapa
     */
    featuresMapId: {
      type: [String, null],
      default: null,
      exposed: true,
    },
    /**
     * {-EN-} List of geojson `features` to be drawn. See [maps](#/developer/PropDetails#features).
     * {-ES-} Lista de `características` de geojson que se dibujarán. Ver [mapas](#/developer/PropDetails#features).
     */
    features: {
      type: [Array, null],
      default: null,
      exposed: true,
    },
    /**
     * {-EN-} Name of the Id property in the `features`. See [maps](#/developer/PropDetails#valueFormat).
     * {-ES-} Nombre de la propiedad Id en las "características". Ver [mapas](#/developer/PropDetails#valueFormat).
     */
    featureIdProperty: {
      type: String,
      default: 'id',
      exposed: true,
    },
    /**
     * {-EN-} Name of the Label property in the `features`. See [maps](#/developer/PropDetails#valueFormat).
     * {-ES-} Nombre de la propiedad Etiqueta en las `características`. Ver [mapas](#/developer/PropDetails#valueFormat).
     */
    featureLabelProperty: {
      type: String,
      default: 'name',
      exposed: true,
    },
    /**
     * {-EN-} List of displaced `features` to be shown in a separate box. See [maps](#/developer/PropDetails#valueFormat).
     * {-ES-} La lista de `características` desplazadas se mostrará en un cuadro separado. Ver [mapas](#/developer/PropDetails#valueFormat).
     */
    displacedFeatures: {
      type: Array,
      default: [],
      exposed: true,
    },
    /**
     * {-EN-} List of pointed `features` to be shown in a custom coordinates. See [maps](#/developer/PropDetails#valueFormat).
     * {-ES-} Lista de `características` de puntos que se mostrarán en coordenadas personalizadas. Ver [mapas](#/developer/PropDetails#valueFormat).
     */
    pointFeatures: {
      type: Array,
      default: [],
      exposed: true,
    },
    /**
     * {-EN-} Name of a d3 projection function or custom projection function. See [maps](#/developer/PropDetails#valueFormat).
     * {-ES-} Nombre de una función de proyección d3 o función de proyección personalizada. Ver [mapas](#/developer/PropDetails#valueFormat).
     */
    projection: {
      type: [String, Function],
      default: 'd3_geoMercator',
      exposed: true,
    },
  }

  static helpers = [RootHelper, ItemsHelper, SizeHelper, SelectableHelper]
  beforeHelpers() {
    // this.content = this.root.selectAll('.content').data([null])
    // this.content = this.content.merge(this.content.enter().append('g').attr('class','content'))
    // this.content.attr('transform',`translate(${this.size.width/2},${this.size.height/2})`)
  }
  draw() {
    const self = this
    function fillTween(d) {
      const fill = self.fill(d)
      if (fill?.includes('url'))
        return function (t) {
          return fill
        }
      else {
        const currentFill = d3_select(this).attr('fill')
        var i = d3_interpolateRgb(currentFill, fill)
        return function (t) {
          return i(t)
        }
      }
    }

    let features = this.config.features
    let featureIdProperty = this.config.featureIdProperty
    this.config.pointFeatures.forEach(pointFeature => {
      const feature = features.find(f => f.properties[featureIdProperty] === pointFeature.id)
      if (!!feature) {
        feature.geometry.type = 'Point'
        feature.geometry.coordinates = pointFeature.coordinates
      }
    })
    let specialRegions = this.config.displacedFeatures || [] //['USA-3563','USA-3517']

    let regionGroups = {
      default: {
        type: 'FeatureCollection',
        features: features.filter(f => specialRegions.indexOf(f.properties[featureIdProperty]) < 0),
      },
      ...specialRegions.reduce((ob, id) => {
        ob[id] = features.find(f => f.properties[featureIdProperty] === id)
        return ob
      }, {}),
    }
    let defaultMargin = 20
    let defaultSize = { width: this.size.width, height: this.size.height - defaultMargin }

    let short = Math.min(this.size.width, this.size.height)
    let boxWidth =
      Object.keys(regionGroups).length > 1
        ? (short / (Object.keys(regionGroups).length - 1)) * 0.24
        : 0
    let boxMargin = Object.keys(regionGroups).length > 1 ? short * 0.08 : 0

    let totalBoxWidth = boxWidth + 2 * boxMargin

    let paths = Object.keys(regionGroups).reduce((result, key, i) => {
      let extent

      if (key === 'default')
        extent = [
          [boxWidth, defaultMargin],
          [defaultSize.width, defaultSize.height],
        ]
      else {
        extent = [
          [totalBoxWidth * (i - 1) + boxMargin, defaultSize.height - boxWidth],
          [totalBoxWidth + totalBoxWidth * (i - 1), defaultSize.height],
        ]
        console.log(`displaced extent ${key}`, extent)
      }
      let projFunc =
        typeof this.config.projection === 'function'
          ? this.config.projection
          : projections[this.config.projection] || projections['d3_geoMercator']
      let proj = projFunc().fitExtent(extent, regionGroups[key])
      result[key] = d3_geoPath(proj)

      return result
    }, {})
    this.paths = paths
    this.itemsExitTrans.style('opacity', '0')
    this.itemsEnter.append('path').attr('vector-effect', 'non-scaling-stroke')
    this.itemsEnter.select('path').attr('fill', 'transparent').attr('stroke', 'transparent')

    this.itemsEnterTrans
      .select('path')
      .attr(
        'stroke',
        !isDarkMode(this.config, this._parent.accessibilityOptions)
          ? COLORS.SKIN_BACKGROUND
          : COLORS.SKIN_BACKGROUND_DARK
      )
      //.attr('fill', d => (this._colorKey(d) !== null ? this.color(d) : 'rgba(200,200,200,0.15)'))
      .attrTween('fill', function (d) {
        return self._colorKey(d) !== null
          ? fillTween.bind(this)({ ...d, id: d.category })
          : () => 'rgba(200, 200, 200, 0.15)'
      })
    this.itemsTrans
      .select('path')
      //.attr('fill', d => (this._colorKey(d) !== null ? this.color(d) : 'rgba(200,200,200,0.15)'))
      .attrTween('fill', function (d) {
        return self._colorKey(d) !== null
          ? fillTween.bind(this)({ ...d, id: d.category })
          : () => 'rgba(200, 200, 200, 0.15)'
      })

    // this.items.merge(this.itemsEnter).select('path')
    this.itemsEnter.select('path').attr('d', d => {
      let f = features.find(f => f.properties[featureIdProperty] === d.id)
      return (paths[d.id] ? paths[d.id] : paths.default)(f)
    })
    this.itemsTrans
      .select('path')
      .attr('d', d => {
        let f = features.find(f => f.properties[featureIdProperty] === d.id)
        return (paths[d.id] ? paths[d.id] : paths.default)(f)
      })
      .attr(
        'stroke',
        !isDarkMode(this.config, this._parent.accessibilityOptions)
          ? COLORS.SKIN_BACKGROUND
          : COLORS.SKIN_BACKGROUND_DARK
      )
  }

  getLegendData() {
    if (this.config.ItemsHelper.colorScale === 'categorical') {
      let legendData = {}
      let allData = this.allData || this.data
      let categories = allData.reduce((res, d) => {
        if (d[this.config.ItemsHelper.colorKey]) res.push(d[this.config.ItemsHelper.colorKey])
        return res
      }, [])
      categories = categories.filter((d, i) => categories.indexOf(d) === i)
      let categoryData = categories.map(g => {
        return { id: g, category: g }
      })
      legendData.category = categoryData
      legendData.category.icon = 'circle'
      return legendData
    } else {
      return null
    }
  }
}
